
import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface BlogHeroProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

export default function BlogHero({ searchQuery, setSearchQuery }: BlogHeroProps) {
  const { ref, isVisible } = useScrollAnimation();
  const [inputValue, setInputValue] = useState(searchQuery);

  const handleSearch = () => {
    setSearchQuery(inputValue.trim());
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClear = () => {
    setInputValue('');
    setSearchQuery('');
  };

  return (
    <section className="relative min-h-[70vh] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/271ab2eb392b9b6bc6def4e3b6f28b46.jpg"
          alt="Marketing Blog"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/50"></div>
      </div>

      <div
        ref={ref}
        className={`relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32 w-full transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="max-w-2xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="px-4 py-1.5 bg-[#00989e] text-white text-xs font-semibold rounded-full">
              INSIGHTS & TIPS
            </span>
            <span className="text-white/70 text-sm">Updated Weekly</span>
          </div>
          
          <h1 className="font-['Montserrat'] text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Marketing <span className="text-[#00b4a6]">Insights</span> That Drive Results
          </h1>
          
          <p className="text-xl text-white/80 mb-8 leading-relaxed">
            Expert strategies, industry trends, and actionable tips to help your brand thrive in the digital landscape.
          </p>

          <div className="relative max-w-xl mb-8">
            <div className="flex items-center bg-white/10 backdrop-blur-md border border-white/20 rounded-full overflow-hidden transition-all duration-300 focus-within:bg-white/20 focus-within:border-white/40">
              <div className="w-5 h-5 flex items-center justify-center ml-5">
                <i className="ri-search-line text-white/60 text-lg"></i>
              </div>
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search articles by keyword..."
                className="flex-1 bg-transparent text-white text-sm placeholder-white/50 px-4 py-3.5 outline-none"
              />
              {inputValue && (
                <button
                  onClick={handleClear}
                  className="w-5 h-5 flex items-center justify-center mr-2 cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <i className="ri-close-circle-fill text-white/50 text-lg"></i>
                </button>
              )}
              <button
                onClick={handleSearch}
                className="px-6 py-3.5 bg-[#00989e] text-white text-sm font-semibold hover:bg-[#007a7f] transition-colors cursor-pointer whitespace-nowrap rounded-r-full"
              >
                Search
              </button>
            </div>
            {searchQuery && (
              <div className="mt-3 flex items-center gap-2">
                <span className="text-white/60 text-sm">Showing results for:</span>
                <span className="px-3 py-1 bg-[#00989e]/30 text-[#00b4a6] text-sm font-medium rounded-full flex items-center gap-1.5">
                  &ldquo;{searchQuery}&rdquo;
                  <button onClick={handleClear} className="cursor-pointer hover:text-white transition-colors">
                    <i className="ri-close-line text-xs"></i>
                  </button>
                </span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <i className="ri-article-line text-[#00b4a6]"></i>
              <span className="text-white text-sm font-medium">50+ Articles</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <i className="ri-eye-line text-[#00b4a6]"></i>
              <span className="text-white text-sm font-medium">10K+ Readers</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <i className="ri-star-line text-[#00b4a6]"></i>
              <span className="text-white text-sm font-medium">Expert Authors</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
